import useCreateProposalLineItem from '@/api/mutations/proposalLineItem/useCreateProposalLineItem';
import useGetMyTaxRates from '@/api/queries/me/useGetMyTaxRates';
import InfiniteLineItemsList from '@/ui/components/infiniteLineItemsList/InfiniteLineItemsList';
import { AnchorButton } from '@payaca/ambrosia';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

export type TProps = {
  proposalId: string;
  groupKey: string;
  backTo: string;
};

const ProposalItemPicker: FC<TProps> = (props) => {
  const { proposalId, groupKey, backTo } = props;

  const history = useHistory();
  const { taxRates, defaultTaxRate } = useGetMyTaxRates();

  // Not every Account has a tax rate marked as default, so fall back to the
  // first it can use rather than leaving the new line item without one.
  const taxRateId = (defaultTaxRate ?? taxRates?.[0])?.id;

  const createProposalLineItem = useCreateProposalLineItem({ proposalId });

  const createOneOffLineItem = () => {
    if (!taxRateId) return;

    const publicId = uuid();
    createProposalLineItem.mutate(
      {
        proposalId,
        groupKey,
        publicId,
        item: {
          new: {
            description: '',
            quantity: 1,
            price: { unitPriceExcTax: 0, taxRateId },
          },
        },
      },
      { onSuccess: () => history.push(`${backTo}/${publicId}`) }
    );
  };

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="px-3.5 pt-3.5">
        <AnchorButton as={Link} to={backTo} variant="link">
          Back to items
        </AnchorButton>
      </div>

      <div
        className={clstx(
          'flex min-h-0 flex-1 flex-col transition-opacity',
          createProposalLineItem.isPending && 'pointer-events-none opacity-60'
        )}
        aria-busy={createProposalLineItem.isPending}
      >
        <InfiniteLineItemsList
          // A new line item must carry a tax rate, so creating one is offered
          // only once the Account's tax rates have loaded.
          onCreateNewItem={taxRateId ? createOneOffLineItem : undefined}
          onLineItemClick={(lineItem) => {
            createProposalLineItem.mutate(
              {
                proposalId,
                groupKey,
                item: { lineItemId: lineItem.publicId },
              },
              { onSuccess: () => history.push(backTo) }
            );
          }}
        />
      </div>
    </div>
  );
};

export default ProposalItemPicker;
